import { Text } from '@chakra-ui/react'
import {
  type CSSProperties,
  type ChangeEvent,
  type KeyboardEvent,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { FilterMenuBase, FilterMenuSection } from './FilterMenuBase'
import { FilterMenuInput } from './FilterMenuInput'

interface InputFilterMenuProps {
  /**
   * The label to display on the menu button
   */
  buttonLabel: string

  /**
   * The header title to show in the menu
   */
  headerTitle?: string

  /**
   * Checks that the input value is valid.
   * This function can be used to validate the input value before updating the search value.
   * If provided, it will be called with the current input value when the user types.
   * If it returns false, the search value will not be updated.
   * @param {string} value
   * @returns {boolean} true if the value is valid, false otherwise
   */
  onChangeValueValidator?: (value: string) => boolean

  /**
   * Callback function triggered when a search is submitted (on Enter key press)
   */
  onSearch: (value: string) => void

  /**
   * The currently selected value
   */
  selectedValue: string | null

  /**
   * Placeholder text for the input field
   */
  placeholder?: string

  /**
   * Hint text to display below the input field
   */
  hint?: string

  /**
   * Custom class name for additional styling
   */
  className?: string

  /**
   * Additional styles for the component
   */
  style?: CSSProperties
}

/**
 * A specialized filter menu that allows direct input and search
 * without providing dropdown options. The user types a value and presses
 * Enter to execute the search.
 */
export function InputFilterMenu({
  buttonLabel,
  headerTitle,
  onChangeValueValidator,
  onSearch,
  selectedValue,
  placeholder,
  hint,
  className = '',
  style = {},
}: InputFilterMenuProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Use translation or fall back to default values
  const translatedHeaderTitle =
    headerTitle ||
    (t('components.inputFilterMenu.filterBy', { label: buttonLabel }) as string)
  const translatedPlaceholder =
    placeholder ||
    (t('components.inputFilterMenu.typePlaceholder', {
      label: buttonLabel,
    }) as string)
  const translatedHint =
    hint ||
    (t('components.inputFilterMenu.enterHint', {
      label: buttonLabel,
    }) as string)

  // Handle key press in the input
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // We now handle Enter in the onKeyDown prop directly
    if (e.key !== 'Enter') {
      e.stopPropagation() // Prevent menu interaction
    }
  }

  // Clear input when menu closes
  const handleMenuClose = () => {
    setInputValue('')
  }

  // Focus on the input field when menu opens
  const handleMenuOpen = () => {
    // Use a small timeout to ensure the menu is fully rendered
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 10)
  }

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.trim()
    if (onChangeValueValidator && !onChangeValueValidator(value)) {
      // If the value is invalid, do not update the input value
      return
    }
    // Update the input value
    setInputValue(value)
  }

  return (
    <FilterMenuBase
      buttonLabel={buttonLabel}
      headerTitle={translatedHeaderTitle}
      className={className}
      style={style}
      hasSelectedValue={!!selectedValue}
      menuWidth={300}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
    >
      {({ closeMenu }) => (
        /* Search input */
        <FilterMenuSection>
          <FilterMenuInput
            value={inputValue}
            onChange={onChange}
            onKeyDown={(e) => {
              // Close on Enter if there's a value
              if (e.key === 'Enter' && inputValue.trim()) {
                onSearch(inputValue)
                closeMenu()
              } else {
                // Handle other keyboard events
                handleKeyDown(e)
              }
            }}
            placeholder={translatedPlaceholder}
            inputRef={inputRef}
            onClear={() => setInputValue('')}
          />
          <Text fontSize="xs" color="gray.500" mt={1}>
            {translatedHint}
          </Text>
        </FilterMenuSection>
      )}
    </FilterMenuBase>
  )
}
